import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Hexagon,
  Grid3x3,
  Flame,
  Magnet,
  ChevronDown,
  X,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { useH3Store } from '@/stores/h3-store';
import { useCotStore } from '@/stores/cot-store';
import { eventsInCell, resolutionLabel } from '@/map/h3/h3-utils';
import { cn } from '@/lib/utils';

const MIN_RES = 4;
const MAX_RES = 11;

/** Stagger config for the panel body revealing row-by-row when expanded. */
const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.035, delayChildren: 0.04 } },
};
const itemVariants = {
  hidden: { opacity: 0, x: 8 },
  show: { opacity: 1, x: 0 },
};

/** H3 hex grid control panel — a collapsible tactical HUD chip, top-right of the map. */
export function H3Controls(): React.JSX.Element {
  const [open, setOpen] = useState(false);

  const gridVisible = useH3Store((s) => s.gridVisible);
  const heatVisible = useH3Store((s) => s.heatVisible);
  const snapUnits = useH3Store((s) => s.snapUnits);
  const resolution = useH3Store((s) => s.resolution);
  const selectedCell = useH3Store((s) => s.selectedCell);
  const overCellLimit = useH3Store((s) => s.overCellLimit);
  const setGridVisible = useH3Store((s) => s.setGridVisible);
  const setHeatVisible = useH3Store((s) => s.setHeatVisible);
  const setSnapUnits = useH3Store((s) => s.setSnapUnits);
  const setResolution = useH3Store((s) => s.setResolution);
  const setSelectedCell = useH3Store((s) => s.setSelectedCell);

  const events = useCotStore((s) => s.events);
  const selectedEvents = selectedCell
    ? eventsInCell(events, selectedCell, resolution)
    : [];

  const anyActive = gridVisible || heatVisible || snapUnits;
  const showWarning = overCellLimit && (gridVisible || heatVisible);

  return (
    <div className="flex justify-end" style={{ perspective: 800 }}>
      <AnimatePresence mode="wait" initial={false}>
        {!open ? (
          <motion.button
            key="chip"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.14 }}
            style={{ transformOrigin: 'top right' }}
            className={cn(
              'group flex items-center gap-2 rounded-lg border bg-echo-grey/90 px-2.5 py-2 shadow-lg backdrop-blur transition-colors',
              anyActive
                ? 'border-echo-yellow/50'
                : 'border-echo-border hover:border-echo-border/80',
            )}
          >
            <Hexagon
              className={cn(
                'h-4 w-4 transition-colors',
                anyActive ? 'fill-echo-yellow/20 text-echo-yellow' : 'text-muted-foreground',
              )}
            />
            <span className="font-chakra text-xs font-semibold uppercase tracking-wider text-foreground">
              Hex
            </span>
            {anyActive && (
              <span className="flex items-center gap-1">
                {gridVisible && <Dot className="bg-echo-yellow" />}
                {heatVisible && <Dot className="bg-orange-400" />}
                {snapUnits && <Dot className="bg-sky-400" />}
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-y-0.5" />
          </motion.button>
        ) : (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            style={{ transformOrigin: 'top right' }}
            className="w-56 overflow-hidden rounded-lg border border-echo-border bg-echo-grey/92 text-xs text-foreground shadow-2xl ring-1 ring-black/20 backdrop-blur"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-2.5 transition-colors hover:bg-echo-border/25"
            >
              <Hexagon className="h-4 w-4 fill-echo-yellow/20 text-echo-yellow" />
              <span className="flex-1 text-left font-chakra text-xs font-semibold uppercase tracking-[0.18em] text-foreground">
                Hex Grid
              </span>
              <ChevronDown className="h-3.5 w-3.5 rotate-180 text-muted-foreground" />
            </button>

            <div className="h-px bg-gradient-to-r from-transparent via-echo-border to-transparent" />

            <motion.div
              variants={listVariants}
              initial="hidden"
              animate="show"
              className="space-y-0.5 p-2"
            >
              <motion.div variants={itemVariants}>
                <ToggleRow icon={Grid3x3} label="Grid overlay" value={gridVisible} onChange={setGridVisible} />
              </motion.div>
              <motion.div variants={itemVariants}>
                <ToggleRow icon={Flame} label="Heat shading" value={heatVisible} onChange={setHeatVisible} />
              </motion.div>
              <motion.div variants={itemVariants}>
                <ToggleRow icon={Magnet} label="Snap to hex" value={snapUnits} onChange={setSnapUnits} />
              </motion.div>

              <motion.div variants={itemVariants} className="px-1 pb-1 pt-2">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Resolution
                  </span>
                  <span className="rounded bg-echo-border/50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-echo-yellow">
                    R{resolution} · {resolutionLabel(resolution)}
                  </span>
                </div>
                <input
                  type="range"
                  min={MIN_RES}
                  max={MAX_RES}
                  step={1}
                  value={resolution}
                  onChange={(e) => setResolution(parseInt(e.target.value, 10))}
                  className="h-1 w-full cursor-pointer accent-echo-yellow"
                  aria-label="Hex grid resolution"
                />
                <div className="mt-1 flex justify-between text-[9px] uppercase tracking-wide text-muted-foreground/70">
                  <span>Coarse</span>
                  <span>Fine</span>
                </div>
              </motion.div>
            </motion.div>

            {showWarning && (
              <div className="mx-2 mb-2 flex items-start gap-1.5 rounded-md border border-amber-600/40 bg-amber-950/40 px-2 py-1.5 text-[10px] leading-snug text-amber-300/90">
                <TriangleAlert className="mt-px h-3 w-3 shrink-0" />
                <span>Too many cells — zoom in or lower the resolution.</span>
              </div>
            )}

            {selectedCell && (
              <SelectedCell
                cellId={selectedCell}
                events={selectedEvents}
                onClear={() => setSelectedCell(null)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Small status indicator dot used on the collapsed chip. */
function Dot({ className }: { className?: string }): React.JSX.Element {
  return <span className={cn('h-1.5 w-1.5 rounded-full', className)} />;
}

interface ToggleRowProps {
  icon: LucideIcon;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

/** A single icon-led toggle row with a slim switch. */
function ToggleRow({ icon: Icon, label, value, onChange }: ToggleRowProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors',
        value ? 'bg-echo-yellow/10' : 'hover:bg-echo-border/30',
      )}
    >
      <Icon
        className={cn('h-3.5 w-3.5 shrink-0 transition-colors', value ? 'text-echo-yellow' : 'text-muted-foreground')}
      />
      <span className={cn('flex-1 text-left transition-colors', value ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
      <span
        className={cn(
          'relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors',
          value ? 'bg-echo-yellow' : 'bg-echo-border',
        )}
      >
        <span
          className={cn(
            'absolute h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform',
            value ? 'translate-x-3' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  );
}

interface SelectedCellProps {
  cellId: string;
  events: { uid: string; callsign?: string }[];
  onClear: () => void;
}

/** Compact readout for the currently selected hex cell and the units inside it. */
function SelectedCell({ cellId, events, onClear }: SelectedCellProps): React.JSX.Element {
  return (
    <div className="mx-2 mb-2 rounded-md border border-echo-border/80 bg-black/40 p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-mono text-[10px] text-muted-foreground">{cellId}</span>
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="shrink-0 text-muted-foreground transition-colors hover:text-echo-yellow"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="font-mono text-sm font-semibold leading-none text-echo-yellow">{events.length}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          unit{events.length === 1 ? '' : 's'} in cell
        </span>
      </div>
      {events.slice(0, 5).map((e) => (
        <div key={e.uid} className="mt-0.5 truncate text-[10px] text-muted-foreground">
          {e.callsign ?? e.uid}
        </div>
      ))}
      {events.length > 5 && (
        <div className="mt-0.5 text-[10px] text-muted-foreground/70">+{events.length - 5} more</div>
      )}
    </div>
  );
}
